import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * EquipmentPage - Equipment Management
 *
 * Configuration page for managing equipment records with
 * bulk import/export and CRUD operations.
 * Includes real-time command progress tracking via SSE.
 */
import { useState, useCallback, useRef } from 'react';
import { ActionIcon, Box, Button, Group, Menu, Modal, Progress, Stack, Table, Text, TextInput, Tooltip, Select, } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEquipment, useImportEquipment, useCreateEquipment, useUpdateEquipment, useDeleteEquipment, useDepartments, useStunRadio, useUnstunRadio } from '@commander/api-client';
import { StatusBadge } from '@commander/ui';
import { IconPlus, IconSearch, IconUpload, IconDownload, IconEdit, IconTrash, IconRadioOff, IconRadioOn } from '@/components/icons';
import { BulkImportModal } from '@/components/BulkImportModal';
import { PanelHeader } from '@/components/PanelHeader';
import { useCommanderEvents } from '@/providers/EventStreamProvider';
/* ====================================
   CSS Keyframe Animations (injected once)
   Premium animations for table rows, hover accents,
   and stunned indicator pulse.
   ==================================== */
const equipmentKeyframes = `
  @keyframes eqRowSlideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes stunnedPulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.3);
    }
  }

  @keyframes eqPageFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
// Inject keyframes into document head (only once)
if (typeof document !== 'undefined') {
    const styleId = 'equipment-keyframes';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = equipmentKeyframes;
        document.head.appendChild(style);
    }
}
/**
 * StunIndicator - displays stun state as either "Stunned" or "Unstunned".
 *
 * Per customer feedback: only two valid states exist.
 * Any state that is not "stunned" (including "active", "unknown", or
 * any other value) is treated as "Unstunned" with a green indicator.
 * "Stunned" items receive a subtle pulse animation on the dot to
 * draw attention to the disabled state.
 */
function StunIndicator({ state }) {
    const isStunned = state === 'stunned';
    const config = isStunned
        ? { colour: '#dc2626', bg: 'rgba(220, 38, 38, 0.08)', label: 'Stunned' }
        : { colour: '#059669', bg: 'rgba(5, 150, 105, 0.08)', label: 'Unstunned' };
    return (_jsxs(Box, { style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 6,
            backgroundColor: config.bg,
        }, children: [_jsx(Box, { style: {
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: config.colour,
                    /* Premium: subtle pulse on the dot for stunned items to draw attention */
                    animation: isStunned ? 'stunnedPulse 2s ease-in-out infinite' : 'none',
                } }), _jsx(Text, { size: "xs", fw: 400, style: { color: config.colour }, children: config.label })] }));
}
/** Numeric ordering of stages — progress only ever advances forward. */
const STAGE_ORDER = {
    Pending: 0,
    Sent: 1,
    Received: 2,
    Transmitting: 3,
    Transmitted: 4,
    Acknowledged: 5,
    NegativeAcknowledged: 5,
};
/** Progress bar percentage for each stage. */
const STAGE_PROGRESS = {
    Pending: 5,
    Sent: 20,
    Received: 40,
    Transmitting: 60,
    Transmitted: 80,
    Acknowledged: 100,
    NegativeAcknowledged: 100,
};
/** Human-readable stage labels. */
const STAGE_LABEL = {
    Pending: 'Sending\u2026',
    Sent: 'Sent to repeater',
    Received: 'Repeater received',
    Transmitting: 'Transmitting\u2026',
    Transmitted: 'Transmitted',
    Acknowledged: 'Acknowledged',
    NegativeAcknowledged: 'Rejected',
};
/** Mantine colour for the progress bar at each stage. */
const STAGE_COLOUR = {
    Pending: 'gray',
    Sent: 'blue',
    Received: 'blue',
    Transmitting: 'orange',
    Transmitted: 'violet',
    Acknowledged: 'green',
    NegativeAcknowledged: 'red',
};
function completedLabel(progress) {
    if (progress.success) {
        return progress.action === 'unstun' ? 'Unstunned' : 'Stunned';
    }
    const code = progress.resultCode;
    if (code === 'Timeout')
        return 'No response (timeout)';
    if (code === 'Unreachable')
        return 'Radio unreachable';
    if (code === 'NegativeAcknowledged')
        return 'Rejected by radio';
    if (code === 'Declined')
        return 'Repeater declined';
    return `Failed — ${code ?? 'Unknown'}`;
}
/** Whether the result code represents an indeterminate outcome (command may have worked). */
function isIndeterminateResult(resultCode) {
    return resultCode === 'Timeout' || resultCode === 'Unreachable';
}
function CommandProgressIndicator({ progress }) {
    if (progress.completed) {
        const indeterminate = !progress.success && isIndeterminateResult(progress.resultCode);
        const colour = progress.success ? 'green' : indeterminate ? 'yellow' : 'red';
        const textColour = progress.success ? '#059669' : indeterminate ? '#d97706' : '#dc2626';
        return (_jsxs(Box, { style: { minWidth: 140 }, children: [_jsx(Progress, { value: 100, color: colour, size: "sm", radius: "xl" }), _jsx(Text, { size: "xs", mt: 4, fw: 500, style: { color: textColour }, children: completedLabel(progress) })] }));
    }
    const pct = STAGE_PROGRESS[progress.stage] ?? 5;
    const colour = STAGE_COLOUR[progress.stage] ?? 'blue';
    const label = STAGE_LABEL[progress.stage] ?? progress.stage;
    return (_jsxs(Box, { style: { minWidth: 140 }, children: [_jsx(Progress, { value: pct, color: colour, size: "sm", radius: "xl", striped: true, animated: true }), _jsx(Text, { size: "xs", mt: 4, c: "dimmed", children: label })] }));
}
const EQUIPMENT_COLUMNS = ['tagId', 'description', 'dmrId', 'departmentId'];
const EQUIPMENT_SAMPLE_DATA = [
    { tagid: 'EQ-001', description: 'Portable Radio Model X', dmrid: '1234567', departmentid: 'ops' },
    { tagid: 'EQ-002', description: 'Handheld Radio Model Y', dmrid: '1234568', departmentid: 'security' },
];
const modalStyles = {
    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
    body: { backgroundColor: 'var(--bg-base)' },
    title: { fontWeight: 600, color: 'var(--text-primary)' },
    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
};
const formInputStyles = {
    label: { color: 'var(--text-primary)', marginBottom: 4 },
    input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
};
function isCompletionEvent(event) {
    return event.type === 'stun.completed'
        || event.type === 'unstun.completed'
        || event.type === 'radio_check.completed';
}
/** Duration (ms) to keep a completed progress indicator visible before clearing. */
const COMPLETION_DISPLAY_MS = 4000;
export function EquipmentPage() {
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newEquipment, setNewEquipment] = useState({ tagId: '', description: '', dmrId: '', departmentId: '' });
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({ tagId: '', description: '', dmrId: '', departmentId: '' });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [stunTarget, setStunTarget] = useState(null);
    // Active command progress per DMR ID
    const [commandProgress, setCommandProgress] = useState({});
    const clearTimersRef = useRef({});
    const { data, isLoading } = useEquipment({ search: debouncedSearch });
    const { data: departments } = useDepartments();
    const importEquipment = useImportEquipment();
    const createEquipment = useCreateEquipment();
    const updateEquipment = useUpdateEquipment();
    const deleteEquipment = useDeleteEquipment();
    const stunRadio = useStunRadio();
    const unstunRadio = useUnstunRadio();
    // Track which commandId maps to which dmrId
    const commandToDmrRef = useRef({});
    // Resolve a DMR ID from an event, matching by commandId first, then by radioId
    // for events that arrive before mutateAsync returns (race condition).
    const resolveDmrId = useCallback((event) => {
        const byCommand = commandToDmrRef.current[event.commandId];
        if (byCommand)
            return byCommand;
        // Fall back: if there's a pending command for this radioId, adopt the commandId
        return event.radioId;
    }, []);
    // Subscribe to SSE events for progress updates
    useCommanderEvents(useCallback((event) => {
        if (event.type === 'command.progress') {
            const dmrId = resolveDmrId(event);
            setCommandProgress((prev) => {
                const existing = prev[dmrId];
                if (!existing || existing.completed)
                    return prev;
                // Only advance forward — never regress to an earlier stage
                const newOrder = STAGE_ORDER[event.stage] ?? 0;
                const existingOrder = STAGE_ORDER[existing.stage] ?? 0;
                if (newOrder <= existingOrder)
                    return prev;
                // Adopt the real commandId if we matched by radioId (race condition)
                if (existing.commandId !== event.commandId) {
                    commandToDmrRef.current[event.commandId] = dmrId;
                }
                return { ...prev, [dmrId]: { ...existing, commandId: event.commandId, stage: event.stage } };
            });
        }
        if (isCompletionEvent(event)) {
            const dmrId = resolveDmrId(event);
            setCommandProgress((prev) => {
                const existing = prev[dmrId];
                if (!existing)
                    return prev;
                if (existing.commandId !== event.commandId) {
                    commandToDmrRef.current[event.commandId] = dmrId;
                }
                return {
                    ...prev,
                    [dmrId]: { ...existing, commandId: event.commandId, completed: true, success: event.success, resultCode: event.resultCode },
                };
            });
            // Clear any existing timer for this row before setting a new one
            if (clearTimersRef.current[dmrId]) {
                clearTimeout(clearTimersRef.current[dmrId]);
            }
            // Clear completed indicator after a delay
            clearTimersRef.current[dmrId] = setTimeout(() => {
                setCommandProgress((prev) => {
                    const next = { ...prev };
                    delete next[dmrId];
                    return next;
                });
                delete commandToDmrRef.current[event.commandId];
                delete clearTimersRef.current[dmrId];
            }, COMPLETION_DISPLAY_MS);
        }
    }, [resolveDmrId]));
    const handleImport = useCallback((file) => importEquipment.mutateAsync(file), [importEquipment]);
    const handleCreate = useCallback(async () => {
        try {
            await createEquipment.mutateAsync(newEquipment);
            notifications.show({
                title: 'Equipment Created',
                message: `Successfully created equipment ${newEquipment.tagId}`,
                color: 'green',
            });
            setCreateModalOpen(false);
            setNewEquipment({ tagId: '', description: '', dmrId: '', departmentId: '' });
        }
        catch {
            notifications.show({
                title: 'Error',
                message: 'Failed to create equipment',
                color: 'red',
            });
        }
    }, [createEquipment, newEquipment]);
    const openEdit = useCallback((item) => {
        setEditForm({ tagId: item.tagId, description: item.description, dmrId: item.dmrId, departmentId: item.departmentId });
        setEditTarget(item);
    }, []);
    const handleUpdate = useCallback(async () => {
        if (!editTarget)
            return;
        try {
            await updateEquipment.mutateAsync({ id: editTarget.id, ...editForm });
            notifications.show({ title: 'Equipment Updated', message: `Updated ${editForm.tagId}`, color: 'green' });
            setEditTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Failed to update equipment', color: 'red' });
        }
    }, [updateEquipment, editTarget, editForm]);
    const handleDelete = useCallback(async () => {
        if (!deleteTarget)
            return;
        try {
            await deleteEquipment.mutateAsync(deleteTarget.id);
            notifications.show({ title: 'Equipment Deleted', message: `Deleted ${deleteTarget.tagId}`, color: 'green' });
            setDeleteTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Failed to delete equipment', color: 'red' });
        }
    }, [deleteEquipment, deleteTarget]);
    const handleExport = useCallback(() => {
        if (!data?.items.length) {
            notifications.show({
                title: 'No Data',
                message: 'No equipment to export',
                color: 'yellow',
            });
            return;
        }
        const headers = ['tagId', 'description', 'dmrId', 'departmentId', 'status', 'stunState', 'lastSeenAt'];
        const csv = [
            headers.join(','),
            ...data.items.map((item) => headers.map((h) => String(item[h] || '')).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `equipment-export-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        notifications.show({
            title: 'Export Complete',
            message: `Exported ${data.items.length} equipment records`,
            color: 'green',
        });
    }, [data]);
    const handleStunConfirm = useCallback(async () => {
        if (!stunTarget)
            return;
        const { dmrId, action } = stunTarget;
        const mutation = action === 'stun' ? stunRadio : unstunRadio;
        // Set progress state BEFORE the API call so SSE events arriving
        // while the HTTP request is in-flight have a state entry to match against.
        // Use a placeholder commandId until the response arrives.
        const placeholder = `pending-${dmrId}`;
        setCommandProgress((prev) => ({
            ...prev,
            [dmrId]: { commandId: placeholder, action, stage: 'Pending' },
        }));
        setStunTarget(null);
        try {
            const response = await mutation.mutateAsync(dmrId);
            // Register the real commandId for future event matching
            commandToDmrRef.current[response.commandId] = dmrId;
            // Update the progress entry with the real commandId (if it hasn't
            // already been adopted by an early SSE event via the radioId fallback)
            setCommandProgress((prev) => {
                const existing = prev[dmrId];
                if (!existing)
                    return prev;
                if (existing.commandId === placeholder) {
                    return { ...prev, [dmrId]: { ...existing, commandId: response.commandId } };
                }
                return prev;
            });
        }
        catch {
            // API call itself failed — clear progress and notify
            setCommandProgress((prev) => {
                const next = { ...prev };
                delete next[dmrId];
                return next;
            });
            notifications.show({
                title: 'Command Failed',
                message: `Failed to send ${action} command`,
                color: 'red',
            });
        }
    }, [stunTarget, stunRadio, unstunRadio]);
    return (_jsxs(Stack, { gap: "lg", style: { animation: 'eqPageFadeIn 400ms ease-out' }, children: [_jsxs(Box, { children: [_jsx(Text, { style: {
                            fontSize: '1.75rem',
                            fontWeight: 500,
                            letterSpacing: '-0.01em',
                            color: 'var(--text-primary)',
                        }, children: "Equipment Registry" }), _jsx(Text, { style: {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: 'var(--text-muted)',
                            marginTop: 4,
                        }, children: "Manage tracked equipment and radio assignments" })] }), _jsxs(Box, { style: {
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-colour)',
                    borderRadius: 6,
                    overflow: 'hidden',
                }, children: [_jsx(PanelHeader, { title: "Equipment Inventory", accentColour: "#1e293b", children: _jsxs(Group, { gap: "sm", children: [_jsxs(Menu, { shadow: "md", width: 200, children: [_jsx(Menu.Target, { children: _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconDownload, { size: 14 }), children: "Export" }) }), _jsx(Menu.Dropdown, { children: _jsx(Menu.Item, { onClick: handleExport, children: "Export to CSV" }) })] }), _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconUpload, { size: 14 }), onClick: () => setImportModalOpen(true), children: "Import" }), _jsx(Button, { variant: "light", size: "xs", leftSection: _jsx(IconPlus, { size: 14 }), color: "blue", onClick: () => setCreateModalOpen(true), children: "Add Equipment" })] }) }), _jsx(Box, { style: {
                            padding: '1rem',
                            borderBottom: '1px solid var(--border-colour)',
                            background: 'var(--bg-base)',
                        }, children: _jsx(TextInput, { placeholder: "Search by tag ID, DMR ID, or description...", leftSection: _jsx(IconSearch, { size: 16, style: { color: 'var(--text-muted)' } }), value: search, onChange: (e) => setSearch(e.target.value), styles: {
                                input: {
                                    backgroundColor: 'var(--bg-card)',
                                    border: '1px solid var(--border-colour)',
                                    color: 'var(--text-primary)',
                                    '&::placeholder': { color: 'var(--text-muted)' },
                                    '&:focus': { borderColor: 'var(--blue-accent)' },
                                },
                            }, style: { maxWidth: 400 } }) }), _jsxs(Box, { style: { padding: '1rem' }, children: [_jsxs(Table, { styles: {
                                    table: { borderCollapse: 'separate', borderSpacing: 0 },
                                    th: {
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        color: 'var(--text-muted)',
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        background: 'var(--bg-base)',
                                    },
                                    td: {
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        color: 'var(--text-primary)',
                                        transition: 'background 150ms ease',
                                    },
                                    tr: {
                                        '&:hover td': {
                                            background: 'var(--bg-base)',
                                        },
                                    },
                                }, children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, { children: "Tag ID" }), _jsx(Table.Th, { children: "Description" }), _jsx(Table.Th, { children: "DMR ID" }), _jsx(Table.Th, { children: "Status" }), _jsx(Table.Th, { children: "Stun State" }), _jsx(Table.Th, { children: "Last Seen" }), _jsx(Table.Th, { style: { width: 140 }, children: "Actions" })] }) }), _jsx(Table.Tbody, { children: isLoading ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 7, children: _jsx(Text, { ta: "center", py: "xl", size: "sm", c: "dimmed", children: "Loading..." }) }) })) : data?.items.length === 0 ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 7, children: _jsx(Text, { ta: "center", py: "xl", c: "dimmed", children: "No equipment found." }) }) })) : (data?.items.map((item, rowIndex) => {
                                            const progress = commandProgress[item.dmrId];
                                            return (_jsxs(Table.Tr, { style: {
                                                    /* Premium: staggered row entrance with fade-in and slide-up */
                                                    animation: `eqRowSlideIn 350ms ease-out ${rowIndex * 40}ms backwards`,
                                                    /* Premium: left border accent that appears on hover via box-shadow inset */
                                                    transition: 'box-shadow 200ms ease, background-color 150ms ease',
                                                    boxShadow: 'inset 0 0 0 0 transparent',
                                                }, onMouseEnter: (e) => {
                                                    e.currentTarget.style.boxShadow = 'inset 3px 0 0 0 #3B82F6';
                                                }, onMouseLeave: (e) => {
                                                    e.currentTarget.style.boxShadow = 'inset 0 0 0 0 transparent';
                                                }, children: [_jsx(Table.Td, { children: _jsx(Text, { ff: "monospace", fw: 400, size: "sm", c: "dimmed", children: item.tagId }) }), _jsx(Table.Td, { children: _jsx(Text, { fw: 400, children: item.description }) }), _jsx(Table.Td, { children: _jsx(Text, { ff: "monospace", fw: 400, size: "sm", c: "dimmed", children: item.dmrId }) }), _jsx(Table.Td, { children: _jsx(StatusBadge, { status: item.status }) }), _jsx(Table.Td, { children: progress
                                                            ? _jsx(CommandProgressIndicator, { progress: progress })
                                                            : _jsx(StunIndicator, { state: item.stunState }) }), _jsx(Table.Td, { children: _jsx(Text, { size: "sm", ff: "monospace", c: "dimmed", children: item.lastSeenAt
                                                                ? new Date(item.lastSeenAt).toLocaleString()
                                                                : '—' }) }), _jsx(Table.Td, { children: _jsxs(Group, { gap: 4, children: [item.stunState === 'stunned' ? (_jsx(Tooltip, { label: "Unstun Radio", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "green", disabled: !!progress && !progress.completed, onClick: () => setStunTarget({ dmrId: item.dmrId, description: item.description, action: 'unstun' }), children: _jsx(IconRadioOn, { size: 14 }) }) })) : (_jsx(Tooltip, { label: "Stun Radio", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "orange", disabled: !!progress && !progress.completed, onClick: () => setStunTarget({ dmrId: item.dmrId, description: item.description, action: 'stun' }), children: _jsx(IconRadioOff, { size: 14 }) }) })), _jsx(Tooltip, { label: "Edit", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "gray", onClick: () => openEdit(item), children: _jsx(IconEdit, { size: 14 }) }) }), _jsx(Tooltip, { label: "Delete", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "red", onClick: () => setDeleteTarget(item), children: _jsx(IconTrash, { size: 14 }) }) })] }) })] }, item.id));
                                        })) })] }), data?.items.length ? (_jsx(Box, { style: {
                                    marginTop: '1rem',
                                    paddingTop: '0.75rem',
                                    borderTop: '1px solid var(--border-colour)',
                                }, children: _jsxs(Text, { size: "xs", c: "dimmed", ff: "monospace", children: ["Showing ", data.items.length, " of ", data.total, " records"] }) })) : null] })] }), _jsx(BulkImportModal, { opened: importModalOpen, onClose: () => setImportModalOpen(false), title: "Import Equipment", entityType: "equipment", expectedColumns: EQUIPMENT_COLUMNS, onImport: handleImport, sampleData: EQUIPMENT_SAMPLE_DATA }), _jsx(Modal, { opened: !!stunTarget, onClose: () => setStunTarget(null), title: stunTarget?.action === 'stun' ? 'Confirm Stun' : 'Confirm Unstun', size: "sm", styles: modalStyles, children: _jsxs(Stack, { gap: "md", children: [_jsx(Text, { size: "sm", style: { color: 'var(--text-primary)' }, children: stunTarget?.action === 'stun'
                                ? `This will disable the radio for "${stunTarget?.description}" (DMR ID: ${stunTarget?.dmrId}). The radio will not be able to transmit or receive until unstunned.`
                                : `This will re-enable the radio for "${stunTarget?.description}" (DMR ID: ${stunTarget?.dmrId}).` }), stunTarget?.action === 'stun' && (_jsx(Box, { style: {
                                padding: '0.625rem 0.75rem',
                                borderRadius: 6,
                                backgroundColor: 'rgba(234, 179, 8, 0.08)',
                                border: '1px solid rgba(234, 179, 8, 0.2)',
                            }, children: _jsx(Text, { size: "xs", style: { color: '#a16207' }, children: "The stun command is sent over the air via the repeater. The radio must be powered on and within range to receive it." }) })), _jsxs(Group, { justify: "flex-end", mt: "sm", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setStunTarget(null), children: "Cancel" }), _jsx(Button, { color: stunTarget?.action === 'stun' ? 'orange' : 'green', onClick: handleStunConfirm, loading: stunRadio.isPending || unstunRadio.isPending, children: stunTarget?.action === 'stun' ? 'Stun Radio' : 'Unstun Radio' })] })] }) }), _jsx(Modal, { opened: !!editTarget, onClose: () => setEditTarget(null), title: "Edit Equipment", styles: modalStyles, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Tag ID", required: true, value: editForm.tagId, onChange: (e) => setEditForm({ ...editForm, tagId: e.target.value }), styles: formInputStyles }), _jsx(TextInput, { label: "Description", required: true, value: editForm.description, onChange: (e) => setEditForm({ ...editForm, description: e.target.value }), styles: formInputStyles }), _jsx(TextInput, { label: "DMR ID", required: true, value: editForm.dmrId, onChange: (e) => setEditForm({ ...editForm, dmrId: e.target.value }), styles: formInputStyles }), _jsx(Select, { label: "Department", data: departments?.items.map(d => ({ value: d.id, label: d.name })) || [], value: editForm.departmentId, onChange: (value) => setEditForm({ ...editForm, departmentId: value || '' }), styles: formInputStyles }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setEditTarget(null), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleUpdate, loading: updateEquipment.isPending, children: "Save Changes" })] })] }) }), _jsx(Modal, { opened: !!deleteTarget, onClose: () => setDeleteTarget(null), title: "Delete Equipment", styles: modalStyles, children: _jsxs(Stack, { gap: "md", children: [_jsxs(Text, { size: "sm", style: { color: 'var(--text-primary)' }, children: ["Are you sure you want to delete ", _jsx("strong", { children: deleteTarget?.tagId }), " (", deleteTarget?.description, ")? This action cannot be undone."] }), _jsxs(Group, { justify: "flex-end", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setDeleteTarget(null), children: "Cancel" }), _jsx(Button, { color: "red", onClick: handleDelete, loading: deleteEquipment.isPending, children: "Delete Equipment" })] })] }) }), _jsx(Modal, { opened: createModalOpen, onClose: () => setCreateModalOpen(false), title: "Add Equipment", styles: modalStyles, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Tag ID", placeholder: "EQ-001", required: true, value: newEquipment.tagId, onChange: (e) => setNewEquipment({ ...newEquipment, tagId: e.target.value }), styles: formInputStyles }), _jsx(TextInput, { label: "Description", placeholder: "Portable Radio Model X", required: true, value: newEquipment.description, onChange: (e) => setNewEquipment({ ...newEquipment, description: e.target.value }), styles: formInputStyles }), _jsx(TextInput, { label: "DMR ID", placeholder: "1234567", required: true, value: newEquipment.dmrId, onChange: (e) => setNewEquipment({ ...newEquipment, dmrId: e.target.value }), styles: formInputStyles }), _jsx(Select, { label: "Department", placeholder: "Select department", data: departments?.items.map(d => ({ value: d.id, label: d.name })) || [], value: newEquipment.departmentId, onChange: (value) => setNewEquipment({ ...newEquipment, departmentId: value || '' }), styles: formInputStyles }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setCreateModalOpen(false), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleCreate, loading: createEquipment.isPending, disabled: !newEquipment.tagId || !newEquipment.description || !newEquipment.dmrId, children: "Create Equipment" })] })] }) })] }));
}
//# sourceMappingURL=EquipmentPage.js.map